/**
 * One row in the chat-history listing (§4.2/§4.5/§4.6) — open, inline-rename, and
 * delete-with-confirm for a single thread. Ported from the standalone reference FE's
 * `ThreadSidebar.tsx` `ThreadRow` so the sidebar/history experience matches: a pencil
 * reveals an inline-editable title (Enter/Escape/blur commits), and delete requires an
 * explicit confirm tap (check/close) rather than deleting on the first click.
 */
import React, { useState } from 'react';
import { Icon, FONT } from './atoms';
import type { PCConversationSummary } from '../../../interface/ProductCatalogChatbotInterface';

export const PCThreadRow: React.FC<{
    thread: PCConversationSummary;
    onOpen: () => void;
    onRename: (title: string) => void;
    onDelete: () => void;
}> = ({ thread, onOpen, onRename, onDelete }) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(thread.title);
    const [confirmingDelete, setConfirmingDelete] = useState(false);

    const commitRename = () => {
        setEditing(false);
        const trimmed = draft.trim();
        if (trimmed && trimmed !== thread.title) onRename(trimmed);
        else setDraft(thread.title);
    };

    return (
        <div
            onClick={() => !editing && onOpen()}
            style={{
                position: 'relative', display: 'flex', alignItems: 'center', gap: 8,
                padding: '12px 16px', borderBottom: '1px solid var(--divider)',
                cursor: editing ? 'default' : 'pointer',
            }}
        >
            <Icon name="chat_bubble" size={18} color="var(--t3)" />
            <div style={{ flex: 1, minWidth: 0 }}>
                {editing ? (
                    <input
                        autoFocus
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commitRename}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') commitRename();
                            if (e.key === 'Escape') { setDraft(thread.title); setEditing(false); }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            fontSize: 13, fontFamily: FONT, color: 'var(--t1)', background: 'var(--surface)',
                            border: '1px solid var(--accent)', borderRadius: 6, padding: '3px 6px', outline: 'none', width: '100%',
                        }}
                    />
                ) : (
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {thread.title || 'New chat'}
                    </div>
                )}
                <div style={{ fontSize: 11, color: 'var(--t3)', marginTop: 1 }}>
                    {thread.messageCount} message{thread.messageCount === 1 ? '' : 's'}
                </div>
            </div>

            {!editing && (
                <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                    {confirmingDelete ? (
                        <>
                            <button
                                type="button" title="Confirm delete"
                                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                                style={{ border: 'none', background: 'transparent', color: 'var(--red)', cursor: 'pointer', display: 'flex', padding: 4 }}
                            >
                                <Icon name="check" size={16} />
                            </button>
                            <button
                                type="button" title="Cancel"
                                onClick={(e) => { e.stopPropagation(); setConfirmingDelete(false); }}
                                style={{ border: 'none', background: 'transparent', color: 'var(--t3)', cursor: 'pointer', display: 'flex', padding: 4 }}
                            >
                                <Icon name="close" size={16} />
                            </button>
                        </>
                    ) : (
                        <>
                            <button
                                type="button" title="Rename"
                                onClick={(e) => { e.stopPropagation(); setEditing(true); }}
                                style={{ border: 'none', background: 'transparent', color: 'var(--t3)', cursor: 'pointer', display: 'flex', padding: 4 }}
                            >
                                <Icon name="edit" size={16} />
                            </button>
                            <button
                                type="button" title="Delete chat"
                                onClick={(e) => { e.stopPropagation(); setConfirmingDelete(true); }}
                                style={{ border: 'none', background: 'transparent', color: 'var(--t3)', cursor: 'pointer', display: 'flex', padding: 4 }}
                            >
                                <Icon name="delete" size={16} />
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
